import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [mobileConnected, setMobileConnected] = useState(false);
  const wsRef = useRef(null);
  const sessionIdRef = useRef(null);
  const userIdRef = useRef(null);

  const connect = useCallback((sessionId, userId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    // Convert HTTP to WS
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/api/ws/desktop/${sessionId}?user_id=${userId}`;
    
    console.log('Connecting to WebSocket:', fullUrl);
    
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;
    sessionIdRef.current = sessionId;
    userIdRef.current = userId;

    ws.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📩 WebSocket message:', data);
        
        if (data.type === 'mobile_paired') {
          console.log('📱 Mobile paired!');
          setMobileConnected(true);
        } else if (data.type === 'mobile_disconnected') {
          console.log('📱 Mobile disconnected');
          setMobileConnected(false);
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      console.log('❌ WebSocket disconnected');
      setIsConnected(false);
      setMobileConnected(false);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return ws;
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setMobileConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const value = {
    isConnected,
    mobileConnected,
    setMobileConnected,
    connect,
    disconnect,
    sendMessage
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
